
import { Project, ProjectStatus, Career, CompanyStats, CarouselItem, BrandingSettings, AboutSettings, Speciality, ContactSettings } from './types';

export const DEFAULT_COMPANY_STATS: CompanyStats = {
  yearsExperience: 25,
  projectsCompleted: 500,
  kilometersBuilt: '10k+'
};

export const DEFAULT_BRANDING_SETTINGS: BrandingSettings = {
  logoUrl: '',
  heroImageUrl: ''
};

export const DEFAULT_CONTACT_SETTINGS: ContactSettings = {
  phone: '+1 (555) 482-1000',
  email: 'contact@sai-ssk.com',
  address: '800 Skyline Blvd, Suite 100, Miami, FL 33131'
};

export const DEFAULT_ABOUT_SETTINGS: AboutSettings = {
  foundationTitle: 'A Legacy of Excellence',
  foundationDescription: 'Founded in 1998, Sai SSK Infrastructures began as a small family-owned civil engineering firm with a single focus: building roads that last. Over the past two decades, we have evolved into a premier infrastructure powerhouse, completing over 500 projects across diverse terrains.',
  foundationImage: 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?q=80&w=800&auto=format&fit=crop',
  missionTitle: 'Our Mission',
  missionDescription: 'To deliver world-class infrastructure solutions that exceed client expectations through innovative engineering, sustainable practices, and an uncompromising commitment to safety and quality.',
  missionPoints: ['Innovative Engineering', 'Sustainable Practices', 'Safety First', 'Client Satisfaction'],
  visionTitle: 'Our Vision',
  visionDescription: 'To be the most trusted and influential infrastructure partner globally, recognized for transforming landscapes and improving lives through engineering excellence and visionary leadership.',
  visionPoints: ['Global Trust', 'Landscape Transformation', 'Visionary Leadership', 'Community Impact'],
  profileTitle: 'Company Profile',
  profileDescription: 'Sai SSK Infrastructures is a leading name in the heavy civil engineering sector, specializing in road construction, highway expansion, and urban infrastructure development. With a robust fleet of modern machinery and a team of seasoned engineers, we handle projects of varying scales with unmatched precision and efficiency.',
  profileImage: 'https://images.unsplash.com/photo-1581092160607-ee22621dd758?q=80&w=800&auto=format&fit=crop'
};

export const INITIAL_CAROUSEL_ITEMS: CarouselItem[] = [
  {
    id: 'c-1',
    imageUrl: 'https://images.unsplash.com/photo-1541888946425-d81bb19480c5?q=80&w=1200&auto=format&fit=crop',
    description: 'Advanced paving technology implemented on interstate highways.'
  },
  {
    id: 'c-2',
    imageUrl: 'https://images.unsplash.com/photo-1581092160607-ee22621dd758?q=80&w=1200&auto=format&fit=crop',
    description: 'Precision engineering in modern urban road networks.'
  },
  {
    id: 'c-3',
    imageUrl: 'https://images.unsplash.com/photo-1590483734724-383b85ad0590?q=80&w=1200&auto=format&fit=crop',
    description: 'Durability tests on reinforced bridge approach segments.'
  }
];

export const PROJECTS: Project[] = [
  {
    id: '1',
    title: 'Interstate 95 Expansion',
    location: 'Florida, USA',
    status: ProjectStatus.COMPLETED,
    description: 'A massive 50-mile multi-lane expansion project focusing on high-durability asphalt and smart drainage systems.',
    imageUrl: 'https://picsum.photos/id/1031/800/600',
    stats: [
      { label: 'Length', value: '52 Miles' },
      { label: 'Timeframe', value: '18 Months' }
    ]
  },
  {
    id: '2',
    title: 'Grand Canyon Scenic Way',
    location: 'Arizona, USA',
    status: ProjectStatus.ONGOING,
    description: 'Paving rugged terrain while preserving environmental integrity. Using specialized eco-friendly binders.',
    imageUrl: 'https://picsum.photos/id/1043/800/600',
    stats: [
      { label: 'Progress', value: '65%' },
      { label: 'Team Size', value: '120+' }
    ]
  },
  {
    id: '3',
    title: 'Downtown Metropolitan Hub',
    location: 'Chicago, IL',
    status: ProjectStatus.COMPLETED,
    description: 'Full urban reconstruction including sub-surface utility coordination and decorative concrete paving.',
    imageUrl: 'https://picsum.photos/id/1044/800/600',
    stats: [
      { label: 'Urban Area', value: '12 Blocks' },
      { label: 'Pavement Type', value: 'Reinforced Concrete' }
    ]
  },
  {
    id: '4',
    title: 'Coastal Highway 101 Bridge Support',
    location: 'California, USA',
    status: ProjectStatus.ONGOING,
    description: 'Stabilizing bridge approach roads against coastal erosion using advanced polymer-modified asphalt.',
    imageUrl: 'https://picsum.photos/id/1050/800/600',
    stats: [
      { label: 'Elevation', value: '450ft' },
      { label: 'Deadline', value: 'Q4 2024' }
    ]
  }
];

export const CAREERS: Career[] = [
  {
    id: 'c1',
    title: 'Senior Civil Engineer',
    department: 'Engineering',
    location: 'Miami, FL',
    type: 'Full-time',
    description: 'Lead large-scale road construction projects, overseeing design compliance and structural integrity.'
  },
  {
    id: 'c2',
    title: 'Site Foreman',
    department: 'Operations',
    location: 'Phoenix, AZ',
    type: 'Full-time',
    description: 'Manage day-to-day on-site operations, safety protocols, and equipment scheduling.'
  },
  {
    id: 'c3',
    title: 'Asphalt Specialist',
    department: 'Technical',
    location: 'Various Locations',
    type: 'Contract',
    description: 'Expertise in mix design and paving quality control for high-speed expressways.'
  }
];

export const DEFAULT_SPECIALITIES: Speciality[] = [
  {
    id: 's1',
    title: 'New road development',
    description: 'End-to-end construction of high-quality highways and arterial roads.',
    imageUrl: 'https://images.unsplash.com/photo-1541888946425-d81bb19480c5?q=80&w=800&auto=format&fit=crop'
  },
  {
    id: 's2',
    title: 'Resurfacing',
    description: 'Expert asphalt resurfacing to restore road smoothness and structural integrity.',
    imageUrl: 'https://images.unsplash.com/photo-1581092160607-ee22621dd758?q=80&w=800&auto=format&fit=crop'
  },
  {
    id: 's3',
    title: 'Pavement Quality Concrete (PQC)',
    description: 'High-strength concrete paving for heavy-duty industrial and highway applications.',
    imageUrl: 'https://images.unsplash.com/photo-1590483734724-383b85ad0590?q=80&w=800&auto=format&fit=crop'
  },
  {
    id: 's4',
    title: 'Gutters and drains',
    description: 'Strategic drainage solutions to prevent water logging and ensure road longevity.',
    imageUrl: 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?q=80&w=800&auto=format&fit=crop'
  },
  {
    id: 's5',
    title: 'Ultra Thin White Topping (UTWT)',
    description: 'Innovative concrete overlay technology for rehabilitating existing asphalt pavements.',
    imageUrl: 'https://images.unsplash.com/photo-1581092160607-ee22621dd758?q=80&w=800&auto=format&fit=crop'
  },
  {
    id: 's6',
    title: 'Structures like Box Culvert and Hume Pipe Culvert (HPC)',
    description: 'Robust cross-drainage structures engineered for efficient water management.',
    imageUrl: 'https://images.unsplash.com/photo-1541888946425-d81bb19480c5?q=80&w=800&auto=format&fit=crop'
  }
];
